/**
 * A non-negative `Price` number read off an object whose static type doesn't expose
 * it, or `null` when the field is absent or not a usable price.
 *
 * Used for the two price reads the compiler can't do directly, both in the new-visit
 * flow:
 * - the visit-type **quote** (`visitTypeData.Price` — the backend returns `0` when a
 *   package the patient already owns covers this visit type), and
 * - the selected visit's **base** price (`selectedVisit` is untyped legacy
 *   `useState(null)` state, so `selectedVisit.Price` is TS2339).
 *
 * Returning `null` on an unreadable shape lets callers fall back to a safe default
 * rather than charging `0` off a value they could not read.
 */
export const readVisitPrice = (value: unknown): number | null => {
  if (typeof value !== 'object' || value === null || !('Price' in value)) {
    return null
  }

  const { Price } = value

  return typeof Price === 'number' && Number.isFinite(Price) && Price >= 0 ? Price : null
}

/**
 * The price the new-visit flow defaults the cost field to: the backend's quoted
 * price when there is one (`0` for a visit a package already covers), otherwise
 * the base service price. The fallback keeps every non-program booking on the
 * price it charged before, and covers the window where the quote is still in
 * flight.
 *
 * Deliberately takes no feature flag. It used to be gated on
 * `selectProgramVisitBadgeFlag`, which made a *display* kill switch into an
 * input to what the patient is charged: turning the badge off to hide a
 * cosmetic bug would have silently reverted every covered visit from the quoted
 * $0 to the full cash price, billing patients for visits their plan already
 * paid for. A kill switch has to be safe to pull, so the only thing it may
 * suppress is the chip.
 *
 * Nothing is lost by dropping it. The quote comes from the visit-type endpoint
 * for this specific patient, so on a patient with no package it simply *is* the
 * base price, and where the endpoint does not serve `Price` at all
 * `readVisitPrice` already answers `null` and the fallback takes over.
 */
export const resolveDefaultVisitPrice = (
  quotedVisitPrice: number | null,
  baseVisitPrice: number | null | undefined,
): number | null | undefined => {
  return quotedVisitPrice ?? baseVisitPrice
}
